import { useEffect, useRef } from 'react';

interface UseInvestigationUrlSyncParams {
  investigationParam: string | null;
  sessionsLoaded: boolean;
  currentSessionId: string | undefined;
  selectSession: (id: string) => Promise<unknown>;
  setInvestigationParam: (id: string | null) => void;
}

// The `investigation` query param is the source of truth for which investigation
// is open. selectSession fetches it and adds it to the list, so a deep link
// resolves even when the session is absent from the loaded list; a null result
// means the id is genuinely gone, so strip it to self-heal the URL.
//
// It runs when the param changes, not when the open investigation does: a new
// investigation is opened before the URL names it, and syncing in that gap would
// load the previous investigation over the new one.
export function useInvestigationUrlSync({
  investigationParam,
  sessionsLoaded,
  currentSessionId,
  selectSession,
  setInvestigationParam,
}: UseInvestigationUrlSyncParams) {
  const currentSessionIdRef = useRef(currentSessionId);
  useEffect(() => {
    currentSessionIdRef.current = currentSessionId;
  }, [currentSessionId]);

  useEffect(() => {
    if (!investigationParam || !sessionsLoaded) return;
    if (currentSessionIdRef.current === investigationParam) return;
    selectSession(investigationParam).then(result => {
      if (result === null) setInvestigationParam(null);
    });
  }, [investigationParam, sessionsLoaded, selectSession, setInvestigationParam]);
}
